import CheckIcon from '@mui/icons-material/Check';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import { Checkbox, FormControlLabel, Typography } from '@mui/material';
import { useEffect, useRef, useState } from 'react';
import { Controller } from 'react-hook-form';
import { Drawer } from 'src/components/drawer';
import { SidebarHeader } from 'src/components/header';
import { AssemblyStatesStepper } from 'src/components/stepper';
import { CodJourney } from 'src/constants/assemblyJourneys';
import { DataTestID, lang } from 'src/utils';

import { LayoutEditSidebarDrawerProps } from './LayoutEditSidebarDrawer.interface';
import {
  CodStepperContainer,
  CustomAlert,
  EditOrCreateButton,
  SidebarContainer,
  SidebarContent,
  SidebarScreenContainer,
  TakeoverOptionsContainer,
  TakeoverScreenContainer,
} from './LayoutEditSidebarDrawer.styled';

export const LayoutEditSidebarDrawer = (
  props: LayoutEditSidebarDrawerProps,
) => {
  const takeoverDefaultState = useRef<boolean[]>([]);
  const [isTakeoverDirty, setIsTakeoverDirty] = useState(false);

  // initial takeover state
  useEffect(() => {
    if (takeoverDefaultState.current.length === 0 && props.openTakeoverDrawer) {
      const takeoverScreens = props.screens.map(
        (screen, index) =>
          props.formMethods.getValues(`screen_${index}`) as boolean,
      );

      takeoverDefaultState.current = takeoverScreens;
    }
  }, [props.screens, props.formMethods, props.openTakeoverDrawer]);

  // Order in which to list the screens
  const displayOrder =
    props.screenDisplayOrder ?? props.screens?.map((_, index) => index) ?? [];

  return (
    <>
      <Drawer
        open={props.openDefaultDrawer}
        data-testid={DataTestID.LayoutEditSidebarDefaultLayoutDrawer}>
        <SidebarContainer>
          <SidebarHeader
            label={lang.editLayoutsSidebar.defaultLayout}
            onBackClick={props.onToggleDefaultDrawer}
          />
          <SidebarContent>
            <CustomAlert severity="info" icon={false}>
              {lang.editLayoutsSidebar.dragSectionAlert}
            </CustomAlert>
            {displayOrder.map((index) => {
              const screen = props.screens[index];

              return (
                <SidebarScreenContainer key={index}>
                  <div style={{ display: 'flex' }}>
                    <Typography>{props.screenNames[index]} screen</Typography>
                    {!screen.name.includes('placeholder') && (
                      <CheckIcon
                        style={{ marginLeft: '5px' }}
                        color="success"
                      />
                    )}
                  </div>
                  <EditOrCreateButton
                    onClick={() =>
                      props.handleNavigateToCreateSections(index + 1)
                    }
                    variant={
                      !screen.name.includes('placeholder')
                        ? 'outlined'
                        : 'contained'
                    }
                    disabled={isTakeoverDirty}>
                    {!screen.name.includes('placeholder')
                      ? lang.edit
                      : lang.create}
                  </EditOrCreateButton>
                </SidebarScreenContainer>
              );
            })}

            {props.isCODLayout &&
              (props.isScreensFilled ? (
                <CodStepperContainer>
                  <AssemblyStatesStepper
                    activeAssemblyJourney={
                      props.activeAssemblyJourney as CodJourney
                    }
                    onChange={props.handleClickStep}
                  />
                </CodStepperContainer>
              ) : (
                <CustomAlert
                  data-testid={lang.editLayoutsSidebar.previewCODStates}
                  icon={false}
                  severity="info">
                  {lang.editLayoutsSidebar.previewCODStates}
                </CustomAlert>
              ))}
          </SidebarContent>
        </SidebarContainer>
      </Drawer>
      <Drawer
        open={props.openTakeoverDrawer}
        data-testid={DataTestID.LayoutEditSidebarTakeoverDrawer}>
        <SidebarContainer>
          <SidebarHeader
            label={lang.editLayoutsSidebar.takeOver}
            onBackClick={props.onToggleTakeoverDrawer}
          />
          <SidebarContent>
            <TakeoverScreenContainer>
              <Typography variant="body1">
                {lang.editLayoutsSidebar.takeOverLabel}
              </Typography>
              {displayOrder.map((index) => (
                <TakeoverOptionsContainer key={index}>
                  <Controller
                    key={index}
                    name={`screen_${index}`}
                    control={props.formMethods.control}
                    render={({ field }) => (
                      <FormControlLabel
                        control={
                          <Checkbox
                            {...field}
                            checked={field?.value ?? false}
                            disabled={
                              props.isScreenDisabled(index) ||
                              props.isSidebarOptionsDisabled(index)
                            }
                            onChange={(e) => {
                              field?.onChange(e.target.checked);

                              setIsTakeoverDirty(
                                takeoverDefaultState.current.some(
                                  (defaultState, idx) =>
                                    defaultState !==
                                    props.formMethods.getValues(
                                      `screen_${idx}`,
                                    ),
                                ),
                              );
                            }}
                          />
                        }
                        label={props.screenNames[index] + ' screen'}
                      />
                    )}
                  />
                  {props.isSidebarOptionsDisabled(index) && (
                    <LockOutlinedIcon
                      color="disabled"
                      data-testid={DataTestID.LayoutEditSidebarTakeoverLock}
                    />
                  )}
                </TakeoverOptionsContainer>
              ))}
            </TakeoverScreenContainer>
            <CustomAlert severity="info" icon={false}>
              {lang.editLayoutsSidebar.takeOverAlert}
            </CustomAlert>
          </SidebarContent>
        </SidebarContainer>
      </Drawer>
      <Drawer
        open={props.openScreenRotationDrawer}
        data-testid={DataTestID.LayoutEditSidebarScreenRotationDrawer}>
        <SidebarContainer>
          <SidebarHeader
            label={lang.editLayoutsSidebar.screenRotations}
            onBackClick={props.onToggleScreenRotationDrawer}
          />
          <SidebarContent>
            <CustomAlert severity="info" icon={false}>
              {lang.editLayoutsSidebar.dragSectionAlert}
            </CustomAlert>
            {displayOrder.map((index) => {
              const screen = props.screens[index];
              const hasSectionTimer = screen.sections.some(
                (section) => section.config?.events?.SECTION_TIMER,
              );

              return (
                <SidebarScreenContainer key={index}>
                  <div style={{ display: 'flex' }}>
                    <Typography>
                      {props.screenNames[index] + ' screen'}
                    </Typography>
                  </div>
                  <EditOrCreateButton
                    onClick={() =>
                      props.handleNavigateToCreateRotationSections(index + 1)
                    }
                    disabled={
                      props.isSidebarOptionsDisabled(index) || isTakeoverDirty
                    }
                    variant={hasSectionTimer ? 'outlined' : 'contained'}>
                    {hasSectionTimer ? lang.edit : lang.create}
                  </EditOrCreateButton>
                </SidebarScreenContainer>
              );
            })}
          </SidebarContent>
        </SidebarContainer>
      </Drawer>
    </>
  );
};
